import React, {useMemo} from 'react';
import {Button} from "fearless-common-ui";
import {concepts, Group, Subscription, SubscriptionRule, User, GroupRole} from "fearless-common";
import {Badge, Card, Row, Col} from "react-bootstrap";
import {BoxArrowUpRight} from "react-bootstrap-icons";
import {Link} from "react-router-dom";
import moment from "moment";
import BillingDashboardButton from "./BillingDashboardButton";
import { DataRow } from "../layout";
import {standardDateFormat} from "../../utils/momentHelpers";
import useAuth from "../hooks/useAuth";
import isAdmin from "../../utils/isAdmin";
import {defaultDateFormat} from "../../utils/date";
import {getProductLabel} from "../../libs/productLabel";
import {routes} from "../../utils";
import {externalLinks} from "../../utils/routes";
import ChangeNumberOfSeats from "./ChangeNumberOfSeats";
import photo51logo from '../../assets/images/logos/photo51-Icon-512.png';
import getViaRuleText from "./getViaRuleText";
import ViewSeatHoldersModal from './ViewSeatHoldersModal';

const LABEL_SIZE = 5;

type GroupWithUsers = (Group & { users?: User[] });

export default function SubscriptionCard({
  subscription,
  group: propsGroup,
  reloadSubscriptions,
}: {
  subscription: Subscription;
  group?: GroupWithUsers | null;
  reloadSubscriptions?: () => void;
}) {
    const group: GroupWithUsers | undefined = propsGroup || subscription?.group;
    const { user } = useAuth();

    const product = useMemo(() => {
      if (Array.isArray(subscription?.items) && subscription?.items.length > 0 && subscription?.items[0]?.product?.name) {
        return subscription?.items[0]?.product.name;
      }
      return subscription.name;
    }, [subscription]);

    return (
        <Card className="mb-3 border-0 max-width-700">
            <Card.Body>
                <Row>
                    <Col md={8}>
                      <DataRow label={`${concepts.Subscription} type`} labelSize={LABEL_SIZE}>
                        {typeof subscription?.name === 'string' && `${getProductLabel(subscription.name)}: `}
                        {product}
                      </DataRow>
                        {subscription.group_id && group && (
                            <DataRow label={`${concepts.Group} name`} labelSize={LABEL_SIZE}>
                                {group.name}&nbsp;
                                <Link to={routes.group(group.id)}>
                                    <BoxArrowUpRight />
                                </Link>
                            </DataRow>
                        )}
                        {subscription.created_at && (
                            <DataRow label="Started at" labelSize={LABEL_SIZE}>
                                {moment(subscription.created_at).format(standardDateFormat)}
                            </DataRow>
                        )}
                        {subscription.renewal_at && (
                            <DataRow label="Renewal at" labelSize={LABEL_SIZE}>
                                {moment(subscription.renewal_at).format(standardDateFormat)}
                            </DataRow>
                        )}
                        {subscription.trial_ends_at && (
                            <DataRow label="Trial ends at" labelSize={LABEL_SIZE}>
                                {moment(subscription.trial_ends_at).format(standardDateFormat)}
                            </DataRow>
                        )}
                        {subscription.ends_at ? (
                            <DataRow label="Status" labelSize={LABEL_SIZE}>
                                <span className="text-danger">
                                    Will stop at {moment(subscription.ends_at).format(standardDateFormat)}
                                </span>
                            </DataRow>
                        ) : (
                            <DataRow label="Status" labelSize={LABEL_SIZE}>
                                Active
                            </DataRow>
                        )}
                        {subscription.group_id && group && (
                            <>
                                {typeof group.total_seats === 'number' ? (
                                    <DataRow
                                        label={(
                                            <>
                                                Total seats&nbsp;
                                            </>
                                        )}
                                        labelSize={LABEL_SIZE}
                                        className="d-inline-flex align-items-center"
                                    >
                                        <span className="me-2">{group.total_seats}</span>
                                      {group.role === GroupRole.Manager && (
                                        <ChangeNumberOfSeats
                                          subscriptionId={subscription.id}
                                          usedSeats={group.used_seats || 0}
                                          totalSeats={group.total_seats}
                                          onSuccess={reloadSubscriptions}
                                        />
                                      )}
                                    </DataRow>
                                ) : null}
                                <DataRow
                                    label={(
                                        <>
                                            Used seats&nbsp;
                                        </>
                                    )}
                                    labelSize={LABEL_SIZE}
                                >
                                    {group.used_seats}
                                    <ViewSeatHoldersModal groupId={group.id} />
                                </DataRow>
                            </>
                        )}
                    </Col>
                  <Col className="text-end" md={4}>
                    {subscription.name === 'photo51' && (
                      <img
                        src={photo51logo}
                        alt={`${getProductLabel(subscription.name)} logo`}
                        width={64}
                        className="rounded-circle p-1 bg-white border border-1 mb-3"
                        style={{left: '-32px', top: '8px'}}
                      />
                    )}
                    <br/>
                    {(group?.role === GroupRole.Manager || !group) && (
                      <BillingDashboardButton
                        groupId={subscription.group_id}
                        user={subscription.user}
                        subscriptionId={subscription.id}
                      />
                    )}
                    <br/>
                    {isAdmin(user) && (
                      <Link target="_blank" rel="noreferrer" to={externalLinks.stripeSearch('sub_1MCpnKGPaZ4c5kEDYrff4y9F')}>
                        <Button variant="outline-secondary" className="mt-3 text-nowrap">
                          Show in Stripe&nbsp;
                          <Badge>Admin only</Badge>
                        </Button>
                      </Link>
                    )}
                  </Col>
                </Row>
            </Card.Body>
        </Card>
    );
}

SubscriptionCard.Rule = function SubscriptionCardRule({ rule, user, subscribedUntil }: { rule: SubscriptionRule; user: User; subscribedUntil: string | null }) {
    const via = useMemo(() => getViaRuleText(rule), [rule]);

    return (
        <Card key={rule.id} className="max-width-700 border-0">
            <Card.Body>
                <DataRow label={`${concepts.Subscription} type`}>
                  {getProductLabel(rule.product)} {via}
                </DataRow>
                <DataRow label="Started at:">{moment(user.created_at).format(defaultDateFormat)}</DataRow>
                {subscribedUntil && <DataRow label="Valid until:">{moment(subscribedUntil).format(defaultDateFormat)}</DataRow>}
            </Card.Body>
        </Card>
    )
}
